import { useId, useState, useRef, useEffect } from "react";
import { SeasonLink as Link } from "./SeasonLink";
import { useLocation } from "react-router-dom";
import { useSeason } from "../../context/SeasonContext";
import { useAuth } from "../../context/AuthContext";
import { SEASON_STATUS } from "../../api/seasons";
import styles from "./seasons.module.css";

export function SeasonBar({ admin = false, compact = false }) {
  const switcherId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isAuction = useLocation().pathname.startsWith("/transactions/auction");
  const {
    season,
    seasons,
    selectSeason,
    withSeason,
    archiveEdit,
    setArchiveEdit,
  } = useSeason();
  const { user } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id) => {
    selectSeason(Number(id));
    setIsOpen(false);
  };

  if (!season) return null;

  return (
    <section
      className={compact ? styles.headerSeason : styles.bar}
      aria-label="Выбранный сезон"
    >
      <div className={styles.switcher} ref={dropdownRef}>
        <label htmlFor={switcherId}>Сезон ЮФУ.Про</label>

        <div className={styles.customSelectWrapper}>
          <button
            id={switcherId}
            type="button"
            className={styles.customSelectTrigger}
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={`${switcherId}-listbox`}
          >
            <span className={styles.customSelectValue}>
              {season.title}
              {!compact && ` · ${SEASON_STATUS[season.status]}`}
            </span>
            <svg
              className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1.5L6 6.5L11 1.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isOpen && (
            <ul
              id={`${switcherId}-listbox`}
              className={styles.customSelectDropdown}
              role="listbox"
            >
              {seasons.map((item) => (
                <li
                  key={item.id}
                  role="option"
                  aria-selected={item.id === season.id}
                  className={`${styles.customSelectOption} ${item.id === season.id ? styles.customSelectOptionActive : ""}`}
                  onClick={() => handleSelect(item.id)}
                >
                  {item.title}
                  {!compact && ` · ${SEASON_STATUS[item.status]}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <span className={`${styles.status} ${styles[season.status]}`}>
        {SEASON_STATUS[season.status]}
      </span>

      <Link
        to={withSeason(user ? "/my-season" : "/seasons")}
        className={styles.barLink}
      >
        {user ? "Мой сезон" : "Все сезоны"} <span aria-hidden="true">↗</span>
      </Link>

      {admin &&
        season.status === "completed" &&
        user?.platform_role === "ADMIN" && (
          <label
            className={styles.archiveToggle}
            title={
              archiveEdit
                ? "Изменения будут сохранены в завершённом сезоне."
                : "Разрешить редактирование завершённого сезона."
            }
          >
            <input
              type="checkbox"
              checked={archiveEdit}
              onChange={(event) => setArchiveEdit(event.target.checked)}
            />
            Правки архива
          </label>
        )}

      {!compact && season.status === "completed" && (
        <p className={styles.barNote}>
          {archiveEdit
            ? "Режим правок архива включён. Изменения будут сохранены в завершённом сезоне."
            : isAuction
              ? "Интенсив завершён. Аукцион работает по срокам, указанным в лотах."
              : "Архив сезона: материалы и результаты доступны для просмотра."}
        </p>
      )}

      {!compact && season.status === "draft" && (
        <p className={styles.barNote}>
          Подготовка сезона. Участники пока не видят его материалы.
        </p>
      )}
    </section>
  );
}
